import { Reservation, TransientReservation } from '../models/reservation';
import { ReservationStatusCode } from '../models/reservation-status-code.enum';
import { OnyxEvent } from '../models/event';
import { ReservationCompare } from '../models/reservation-compare';
import { ComparatorUtils } from './comparator-utils';
import { isNullOrUndefined } from 'util';
import { Observable } from 'rxjs/Observable';
import { List } from 'immutable';
import { NumberUtils } from '../../shared/utils/number.util';
import { Party } from '../models/party';

export class ReservationUtils {

  /**
   * Do not include reservation value when the reservation no longer has possible value.
   *
   * If the record has a status of No Show or Cancelled exclude reservations from the calculations.
   *
   * @param reservation
   * @returns {boolean}
   */
  static hasReservationValue(reservation: Reservation): boolean {
    let value = reservation.status;

    if (value) {
      value = value.toUpperCase();
    }

    let returnValue = false;

    switch (value) {
      case ReservationStatusCode.CANCELLED.toString():
        returnValue = false;
        break;
      case ReservationStatusCode.NO_SHOW.toString():
        returnValue = false;
        break;
      default:
        returnValue = true;
    }

    return returnValue;
  }

  static getCommissionRate(vendor: Party): number {
    if (vendor) {
      return vendor.commissionRoomPercentage;
    } else {
      return 0;
    }

  }

  static getRevenue(reservation: Reservation): number {
    if (this.hasReservationValue(reservation)) {
      return reservation.nights * reservation.roomRate * (reservation.rooms > 0 ? reservation.rooms : 1);
    } else {
      return 0;
    }
  }

  static getCommission(reservation: Reservation, vendor: Party): number {
    if (!this.hasReservationValue(reservation)) {
      return 0;
    }

    let revenue = reservation.revenue;

    if (!revenue) {
      revenue = this.getRevenue(reservation);
    }

    return (revenue * this.getCommissionRate(vendor)) / 100;
  }

  static reservationCompareComparator(obj1: ReservationCompare, obj2: ReservationCompare): number {
    let compareValue = ComparatorUtils.compareNullable(obj1, obj2);
    if (compareValue !== 0 || (isNullOrUndefined(obj1) && isNullOrUndefined(obj2))) {
      return compareValue;
    }

    compareValue = ComparatorUtils.compareNullable(obj1.reservation, obj2.reservation);
    if (compareValue !== 0 || (isNullOrUndefined(obj1.reservation) && isNullOrUndefined(obj2.reservation))) {
      return compareValue;
    }

    return ComparatorUtils.compareLastNameFirstName(
      { firstName: obj1.reservation.firstName, lastName: obj1.reservation.lastName },
      { firstName: obj2.reservation.firstName, lastName: obj2.reservation.lastName });
  }

  static rsvpCompareComparator(obj1: ReservationCompare, obj2: ReservationCompare): number {
    let compareValue = ComparatorUtils.compareNullable(obj1, obj2);
    if (compareValue !== 0 || (isNullOrUndefined(obj1) && isNullOrUndefined(obj2))) {
      return compareValue;
    }

    compareValue = ComparatorUtils.compareNullable(obj1.rsvp, obj2.rsvp);
    if (compareValue !== 0 || (isNullOrUndefined(obj1.rsvp) && isNullOrUndefined(obj2.rsvp))) {
      return compareValue;
    }

    return ComparatorUtils.compareLastNameFirstName(
      { firstName: obj1.rsvp.firstName, lastName: obj1.rsvp.lastName },
      { firstName: obj2.rsvp.firstName, lastName: obj2.rsvp.lastName });
  }

  static calculateRevenueTotal(obsReservationList: Observable<List<ReservationCompare>>): Observable<number> {
    return obsReservationList.map((compareList: List<ReservationCompare>) => {
      return compareList
        .filter(compare => !isNullOrUndefined(compare.reservation) && !isNullOrUndefined(compare.reservation.revenue))
        .map(compare => compare.reservation.revenue)
        .reduce((prev: number, current: number) => prev + current, 0);
    }).map(total => NumberUtils.round2Fixed(total));
  }

  static calculateCommissionTotal(obsReservationList: Observable<List<ReservationCompare>>): Observable<number> {
    return obsReservationList.map((compareList: List<ReservationCompare>) => {
      return compareList
        .filter(compare => !isNullOrUndefined(compare.reservation) && !isNullOrUndefined(compare.reservation.commission))
        .map(compare => compare.reservation.commission)
        .reduce((prev: number, current: number) => prev + current, 0);
    }).map(total => NumberUtils.round2Fixed(total));
  }

  static calculateRsvpCommissionTotal(obsReservationList: Observable<List<ReservationCompare>>): Observable<number> {
    return obsReservationList.map((compareList: List<ReservationCompare>) => {
      return compareList
        .filter(compare => !isNullOrUndefined(compare.rsvp) && !isNullOrUndefined(compare.rsvp.commission))
        .map(compare => compare.rsvp.commission)
        .reduce((prev: number, current: number) => prev + current, 0);
    }).map(total => NumberUtils.round2Fixed(total));
  }

  static calculateReservationTotalNights(obsReservationList: Observable<List<ReservationCompare>>): Observable<number> {
    return obsReservationList.map((compareList: List<ReservationCompare>) => {
      return compareList
        .filter(compare => !isNullOrUndefined(compare.reservation) && !isNullOrUndefined(compare.reservation.nights))
        .map(compare => compare.reservation.nights)
        .reduce((prev: number, current: number) => prev + current, 0);
    });
  }

  static isListEmpty(obsReservationList: Observable<List<ReservationCompare>>): Observable<boolean> {
    return ReservationUtils.getListSize(obsReservationList).map(compareListSize => compareListSize === 0);
  }

  static isTransientListEmpty(obsReservationList: Observable<List<TransientReservation>>): Observable<boolean> {
    return ReservationUtils.getTransientListSize(obsReservationList).map(compareListSize => compareListSize === 0);
  }

  static getListSize(obsReservationList: Observable<List<ReservationCompare>>): Observable<number> {
    return obsReservationList.map(compareList => compareList.size);
  }

  static getTransientListSize(obsReservationList: Observable<List<TransientReservation>>): Observable<number> {
    return obsReservationList.map((compareList) => {
      return compareList.size;
    });
  }

  static onlyReservationsFilter(reservationList: List<ReservationCompare>): List<ReservationCompare> {
    return reservationList.filter(compare => compare.reservation ? true : false).toList();
  }

  static onlyRsvpFilter(reservationList: List<ReservationCompare>): List<ReservationCompare> {
    return reservationList.filter(compare => compare.rsvp ? true : false).toList();
  }

  static getCompareByVendor(reservationList: List<ReservationCompare>, eventPartyId: number): List<ReservationCompare> {
    return reservationList.filter(compare => compare.eventPartyId === eventPartyId).toList();
  }

  static getNumberOfRsvps(obsReservationList: Observable<List<ReservationCompare>>): Observable<number> {
    return obsReservationList.map((compareList: List<ReservationCompare>) => {
      return (compareList ? this.onlyRsvpFilter(compareList).size : 0);
    });
  }

  static getNumberOfReservations(obsReservationList: Observable<List<ReservationCompare>>): Observable<number> {
    return obsReservationList.map((compareList: List<ReservationCompare>) => {
      return (compareList ? this.onlyReservationsFilter(compareList).size : 0);
    });
  }
}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/utils/reservation-utils.ts